import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getSupabase, USE_MOCKS } from '../lib/supabase';
import { homeRouteFor, WELCOME_COMPLETED_KEY } from '../../../src/services/team/roles';

const MIN_PASSWORD = 10;

const EXPIRED_MESSAGE =
  'Invite links work once and expire after a while. Ask the person who invited you to resend it from Settings › Team.';
const RESET_EXPIRED_MESSAGE =
  'Reset links work once and expire after a while. Ask for a new one from the sign-in page.';

type OtpType = 'invite' | 'magiclink' | 'recovery';

function otpTypeOf(value: string | null): OtpType {
  return value === 'magiclink' || value === 'recovery' ? value : 'invite';
}

/**
 * Where invite and password-reset links land
 * (DASHBOARD_BASE_URL/welcome?token_hash=…&type=invite|magiclink|recovery).
 * The token is only spent when the person clicks Continue, so a mail scanner
 * opening the link first does not use it up. Then they set a password (and,
 * for invites, their name) so they can sign in normally next time.
 */
export function Welcome(): JSX.Element {
  const { user, loading, role, deactivated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokenHash = USE_MOCKS ? null : params.get('token_hash');
  const otpType = otpTypeOf(params.get('type'));
  // Password reset: from a recovery link, or after its token was spent (?mode=reset).
  const reset = otpType === 'recovery' || params.get('mode') === 'reset';
  const [verifying, setVerifying] = useState(false);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const current = user?.user_metadata?.full_name;
    if (typeof current === 'string') setName(current);
  }, [user]);

  if (loading) {
    return (
      <div className="ari-app" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
        <div className="spinner dark" />
      </div>
    );
  }

  async function accept() {
    if (!tokenHash) return;
    setVerifying(true);
    setLinkErr(null);
    const { error } = await getSupabase().auth.verifyOtp({ token_hash: tokenHash, type: otpType });
    setVerifying(false);
    if (error) {
      setLinkErr(reset ? RESET_EXPIRED_MESSAGE : EXPIRED_MESSAGE);
      return;
    }
    // Signed in; drop the spent token from the address bar.
    navigate(reset ? '/welcome?mode=reset' : '/welcome', { replace: true });
  }

  if (tokenHash) {
    const title = linkErr ? 'This link has expired' : reset ? 'Reset your password' : 'You are invited';
    const intro = reset
      ? 'Continue to choose a new password for your Ariel Operations Dashboard account.'
      : 'You have been invited to the Ariel Operations Dashboard. Continue to set up your account.';
    return (
      <Shell title={title}>
        <div className="login-sub">{linkErr ?? intro}</div>
        {linkErr ? (
          <Link className="btn btn-secondary" to={reset ? '/forgot-password' : '/login'}>
            {reset ? 'Get a new link' : 'Go to sign in'}
          </Link>
        ) : (
          <button type="button" className="btn btn-primary" disabled={verifying} onClick={() => void accept()}>
            {verifying ? <><span className="spinner" />Checking your link…</> : 'Continue'}
          </button>
        )}
      </Shell>
    );
  }

  if (!user || deactivated) {
    return (
      <Shell title={deactivated ? 'Your access is off' : 'This link has expired'}>
        <div className="login-sub">
          {deactivated
            ? 'An admin turned off your access to the Operations Dashboard. Ask them to restore it if you still need it.'
            : reset ? RESET_EXPIRED_MESSAGE : EXPIRED_MESSAGE}
        </div>
        <Link className="btn btn-secondary" to="/login">Go to sign in</Link>
      </Shell>
    );
  }

  const metadata = user.user_metadata ?? {};
  // A reset only asks for a name when none is on file (an invitee who never
  // finished /welcome).
  const askName = !reset || typeof metadata.full_name !== 'string' || !metadata.full_name.trim();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (askName && !name.trim()) return setErr('Add your name so your team knows who you are.');
    if (pw.length < MIN_PASSWORD) return setErr(`Use at least ${MIN_PASSWORD} characters.`);
    if (pw !== pw2) return setErr('The passwords do not match.');
    setSaving(true);
    if (!USE_MOCKS) {
      const { error } = await getSupabase().auth.updateUser({
        password: pw,
        data: {
          ...(askName ? { full_name: name.trim() } : {}),
          // Marks an invite as accepted on Settings › Team (a reset counts too).
          ...(metadata[WELCOME_COMPLETED_KEY] ? {} : { [WELCOME_COMPLETED_KEY]: new Date().toISOString() }),
        },
      });
      if (error) {
        setErr(error.message);
        setSaving(false);
        return;
      }
    }
    navigate(homeRouteFor(role), { replace: true });
  }

  return (
    <Shell title={reset ? 'Choose a new password' : 'Welcome to the dashboard'}>
      <div className="login-sub">
        {reset
          ? `You are signed in as ${user.email}. Choose a new password to use next time you sign in.`
          : `You are signed in as ${user.email}. Add your name and choose a password so you can sign in next time.`}
      </div>
      <form className="login-form" onSubmit={submit}>
        {askName && (
          <div>
            <label className="input-label" htmlFor="welcome-name">Your name</label>
            <input id="welcome-name" className="input" style={{ width: '100%' }} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </div>
        )}
        <div>
          <label className="input-label" htmlFor="welcome-pw">{reset ? 'New password' : 'Password'}</label>
          <input id="welcome-pw" className="input" type="password" style={{ width: '100%' }} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
          <div className="d-meta" style={{ marginTop: 6 }}>At least {MIN_PASSWORD} characters.</div>
        </div>
        <div>
          <label className="input-label" htmlFor="welcome-pw2">Confirm password</label>
          <input id="welcome-pw2" className="input" type="password" style={{ width: '100%' }} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
          {err && <div className="input-error">{err}</div>}
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <><span className="spinner" />Saving…</> : 'Save and continue'}
        </button>
      </form>
    </Shell>
  );
}

export function Shell({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="ari-app">
      <div className="login-page">
        <div className="login-card">
          <div className="login-brand">
            <span className="dot" />
            <span>Ariel Operations Reports</span>
          </div>
          <h1 className="login-h">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}
